import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Modal from 'components/common/modal';
import Tooltip from 'components/layout/tooltip';
import { useState } from 'react';

const equipmentPhotoThumbnailSizes = {
  sm: 'h-20 w-20',
  md: 'h-24 w-24',
  lg: 'h-32 w-32'
} as const;

type EquipmentPhotoThumbnailProps = Readonly<{
  src: string;
  alt: string;
  onRemove: () => void;
  size: keyof typeof equipmentPhotoThumbnailSizes;
}>;

export default function EquipmentPhotoThumbnail({
  src,
  alt,
  onRemove,
  size
}: EquipmentPhotoThumbnailProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const viewLabel = alt ? `View ${alt}` : 'View larger image';
  const removeLabel = alt ? `Remove ${alt}` : 'Remove photo';

  return (
    <div className='relative'>
      <button
        type='button'
        aria-label={viewLabel}
        onClick={() => setPreviewOpen(true)}
        className='relative block cursor-zoom-in'
      >
        <img
          src={src}
          alt={alt}
          className={clsx(
            equipmentPhotoThumbnailSizes[size],
            'rounded object-cover hover:opacity-90'
          )}
          draggable={false}
        />
        <Tooltip text='View larger' />
      </button>
      <button
        className='absolute right-1 top-1 rounded-full bg-white/90 p-0.5 text-gray-600 shadow hover:text-gray-900'
        type='button'
        aria-label={removeLabel}
        onClick={onRemove}
      >
        <XMarkIcon className='h-4 w-4' />
      </button>
      <Modal
        openState={[previewOpen, setPreviewOpen]}
        title={alt || 'Image preview'}
        infoMode
        size='fullscreen'
      >
        <img
          src={src}
          alt={alt}
          className='mx-auto max-h-[70vh] w-auto max-w-full object-contain'
        />
      </Modal>
    </div>
  );
}
